import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { AlertCircle, Layers3, Loader2, PackagePlus, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { formatBusinessDate, formatBusinessDateTime } from '../lib/dateTime';

const InventoryPage = () => {
  const { api, permissions } = useAuth();
  const [products, setProducts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [batchesError, setBatchesError] = useState('');
  const [selectedBatchProduct, setSelectedBatchProduct] = useState('all');
  const [includeDepletedBatches, setIncludeDepletedBatches] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [receiveForm, setReceiveForm] = useState({
    product_id: '',
    quantity_received: '',
    reason: 'Stock received',
    new_cost_price: '',
    batch_number: '',
    expiry_date: '',
    record_as_liability: false,
    supplier_name: '',
    total_cost: '',
    amount_paid: '',
    due_date: '',
    notes: '',
  });

  const [adjustForm, setAdjustForm] = useState({
    product_id: '',
    delta: '',
    reason: '',
  });

  const canAdjustInventory = permissions?.adjust_inventory !== false;

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, adjustmentsRes] = await Promise.all([
        backendApi.products.list(api),
        backendApi.inventory.adjustments(api).catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data || []);
      setAdjustments(adjustmentsRes.data || []);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchBatches = useCallback(async () => {
    setBatchesLoading(true);
    setBatchesError('');
    try {
      const response = await backendApi.inventory.getBatches(api, {
        productId: selectedBatchProduct === 'all' ? undefined : selectedBatchProduct,
        includeDepleted: includeDepletedBatches,
      });
      setBatches(response.data || []);
    } catch (error) {
      setBatches([]);
      setBatchesError(error?.response?.data?.detail || 'Failed to load inventory batches.');
    } finally {
      setBatchesLoading(false);
    }
  }, [api, includeDepletedBatches, selectedBatchProduct]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const productOptions = useMemo(
    () => products.map((p) => ({
      id: p.id,
      label: `${p.name} (${p.sku})`,
      stock: p.stock_quantity,
      track_expiry: Boolean(p.track_expiry),
    })),
    [products],
  );

  const selectedReceiveProduct = useMemo(
    () => products.find((product) => product.id === receiveForm.product_id) || null,
    [products, receiveForm.product_id],
  );

  const receiveRequiresExpiry = Boolean(selectedReceiveProduct?.track_expiry);

  const formatCurrency = (amount) =>
    `UGX ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const onSubmitReceive = async () => {
    if (!receiveForm.product_id || !receiveForm.quantity_received) {
      toast.error('Select product and quantity');
      return;
    }
    if (receiveRequiresExpiry && !receiveForm.expiry_date) {
      toast.error('Expiry date is required for this product');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        product_id: receiveForm.product_id,
        quantity_received: Number(receiveForm.quantity_received),
        reason: receiveForm.reason || 'Stock received',
        new_cost_price: receiveForm.new_cost_price ? Number(receiveForm.new_cost_price) : null,
        batch_number: receiveForm.batch_number || null,
        expiry_date: receiveForm.expiry_date || null,
        record_as_liability: Boolean(receiveForm.record_as_liability),
        supplier_name: receiveForm.supplier_name || null,
        total_cost: receiveForm.total_cost ? Number(receiveForm.total_cost) : null,
        amount_paid: receiveForm.amount_paid ? Number(receiveForm.amount_paid) : 0,
        due_date: receiveForm.due_date ? new Date(receiveForm.due_date).toISOString() : null,
        notes: receiveForm.notes || null,
      };
      const res = await backendApi.inventory.receive(api, payload);
      toast.success(res?.data?.message || 'Stock received');
      setReceiveForm({
        product_id: '',
        quantity_received: '',
        reason: 'Stock received',
        new_cost_price: '',
        batch_number: '',
        expiry_date: '',
        record_as_liability: false,
        supplier_name: '',
        total_cost: '',
        amount_paid: '',
        due_date: '',
        notes: '',
      });
      await Promise.all([fetchData(), fetchBatches()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to receive stock');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitAdjust = async () => {
    if (!adjustForm.product_id || !adjustForm.delta || !adjustForm.reason) {
      toast.error('Provide product, delta, and reason');
      return;
    }
    setSubmitting(true);
    try {
      await backendApi.inventory.adjust(api, {
        product_id: adjustForm.product_id,
        delta: Number(adjustForm.delta),
        reason: adjustForm.reason,
      });
      toast.success('Inventory adjusted');
      setAdjustForm({ product_id: '', delta: '', reason: '' });
      await Promise.all([fetchData(), fetchBatches()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Inventory</h1>
        <p className="text-muted-foreground">Receive stock, apply manual adjustments, and review adjustment history.</p>
      </div>

      {!canAdjustInventory ? (
        <Alert className="border-border/60 bg-background/70">
          <AlertDescription>
            You can view inventory history, but you do not have permission to receive or manually adjust stock.
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue={canAdjustInventory ? 'receive' : 'history'}>
        <TabsList>
          <TabsTrigger value="receive" disabled={!canAdjustInventory}>Receive Stock</TabsTrigger>
          <TabsTrigger value="adjust" disabled={!canAdjustInventory}>Adjust Stock</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="batches">Batches &amp; Expiry</TabsTrigger>
        </TabsList>

        <TabsContent value="receive" className="mt-4">
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PackagePlus className="h-5 w-5" /> Receive Stock
              </CardTitle>
              <CardDescription>Increase stock and optionally record supplier liability.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product</Label>
                  <Select
                    value={receiveForm.product_id}
                    onValueChange={(v) => setReceiveForm((p) => ({ ...p, product_id: v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>
                      {productOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity Received</Label>
                  <Input
                    type="number"
                    value={receiveForm.quantity_received}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, quantity_received: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Input
                    value={receiveForm.reason}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, reason: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Cost Price (optional)</Label>
                  <Input
                    type="number"
                    value={receiveForm.new_cost_price}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, new_cost_price: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Batch / Lot Number</Label>
                  <Input
                    value={receiveForm.batch_number}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, batch_number: e.target.value }))}
                    placeholder="Optional supplier batch number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{receiveRequiresExpiry ? 'Expiry Date *' : 'Expiry Date'}</Label>
                  <Input
                    type="date"
                    value={receiveForm.expiry_date}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, expiry_date: e.target.value }))}
                    disabled={!receiveForm.product_id}
                  />
                </div>
              </div>

              {receiveRequiresExpiry ? (
                <Alert className="border-border/60 bg-background/70">
                  <AlertDescription>
                    This product is marked as expiry-tracked. Each stock receipt should carry the correct batch expiry date.
                  </AlertDescription>
                </Alert>
              ) : null}

              <Alert className="border-border/60 bg-background/70">
                <AlertDescription>
                  To record this stock as credit from supplier, enable the fields below.
                </AlertDescription>
              </Alert>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Record as Stock on credit (true/false)</Label>
                  <Select
                    value={receiveForm.record_as_liability ? 'true' : 'false'}
                    onValueChange={(v) => setReceiveForm((p) => ({ ...p, record_as_liability: v === 'true' }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Supplier Name</Label>
                  <Input
                    value={receiveForm.supplier_name}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, supplier_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Cost</Label>
                  <Input
                    type="number"
                    value={receiveForm.total_cost}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, total_cost: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount Paid</Label>
                  <Input
                    type="number"
                    value={receiveForm.amount_paid}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, amount_paid: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="datetime-local"
                    value={receiveForm.due_date}
                    onChange={(e) => setReceiveForm((p) => ({ ...p, due_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={receiveForm.notes}
                  onChange={(e) => setReceiveForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={onSubmitReceive} disabled={submitting || !canAdjustInventory}>
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackagePlus className="h-4 w-4 mr-2" />}
                  Receive Stock
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="adjust" className="mt-4">
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5" /> Manual Adjustment
              </CardTitle>
              <CardDescription>Use positive/negative delta for corrections and stock counts.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Product</Label>
                  <Select
                    value={adjustForm.product_id}
                    onValueChange={(v) => setAdjustForm((p) => ({ ...p, product_id: v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>
                      {productOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Delta (+/-)</Label>
                  <Input
                    type="number"
                    value={adjustForm.delta}
                    onChange={(e) => setAdjustForm((p) => ({ ...p, delta: e.target.value }))}
                    placeholder="e.g. -2 or 10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Input
                    value={adjustForm.reason}
                    onChange={(e) => setAdjustForm((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="Stock count correction"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={onSubmitAdjust} disabled={submitting || !canAdjustInventory}>
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SlidersHorizontal className="h-4 w-4 mr-2" />}
                  Apply Adjustment
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardHeader>
              <CardTitle>Adjustment History</CardTitle>
              <CardDescription>Latest inventory adjustments and stock receipts.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Delta</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Batch / Expiry</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adjustments.map((adj) => {
                    const product = products.find((p) => p.id === adj.product_id);
                    const delta = Number(adj.delta || 0);
                    return (
                      <TableRow key={adj.id}>
                        <TableCell>{formatBusinessDateTime(adj.created_at)}</TableCell>
                        <TableCell>{product?.name || adj.product_id}</TableCell>
                        <TableCell>
                          <Badge variant={delta >= 0 ? 'default' : 'destructive'}>
                            {delta >= 0 ? `+${delta}` : `${delta}`}
                          </Badge>
                        </TableCell>
                        <TableCell>{adj.reason}</TableCell>
                        <TableCell>
                          <div className="text-sm space-y-1">
                            <p>{adj.batch_number || '-'}</p>
                            <p className="text-muted-foreground">
                              {adj.expiry_date ? formatBusinessDate(adj.expiry_date) : 'No expiry'}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!adjustments.length ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No adjustments recorded yet.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="batches" className="mt-4">
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Layers3 className="h-5 w-5 text-primary" /> Batches &amp; Expiry
                </CardTitle>
                <CardDescription className="mt-1.5">Monitor received stock, remaining quantities, and upcoming expiry dates.</CardDescription>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={fetchBatches} disabled={batchesLoading} className="gap-2 self-start">
                <RefreshCw className={`h-4 w-4 ${batchesLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-col gap-4 rounded-lg bg-muted/30 p-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="w-full space-y-2 sm:max-w-sm">
                  <Label htmlFor="batch-product-filter">Product</Label>
                  <Select value={selectedBatchProduct} onValueChange={setSelectedBatchProduct}>
                    <SelectTrigger id="batch-product-filter"><SelectValue placeholder="All products" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All products</SelectItem>
                      {productOptions.map((product) => <SelectItem key={product.id} value={product.id}>{product.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex min-h-10 items-center gap-3">
                  <Switch id="include-depleted-batches" checked={includeDepletedBatches} onCheckedChange={setIncludeDepletedBatches} />
                  <Label htmlFor="include-depleted-batches" className="cursor-pointer font-medium">Show depleted batches</Label>
                </div>
              </div>

              {batchesLoading ? (
                <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-muted-foreground" role="status">
                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  <p className="text-sm">Loading inventory batches...</p>
                </div>
              ) : batchesError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span>{batchesError}</span>
                    <Button type="button" size="sm" variant="outline" onClick={fetchBatches} className="self-start gap-2 border-destructive/30 sm:self-auto">
                      <RefreshCw className="h-4 w-4" /> Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : batches.length ? (
                <div className="overflow-x-auto rounded-lg border border-border/60">
                  <Table className="min-w-[820px]">
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead>Product</TableHead>
                        <TableHead>Batch Number</TableHead>
                        <TableHead>Expiry Date</TableHead>
                        <TableHead className="text-right">Quantity Received</TableHead>
                        <TableHead className="text-right">Quantity Remaining</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batches.map((batch) => {
                        const product = products.find((item) => item.id === batch.product_id);
                        const remaining = Number(batch.quantity_remaining || 0);
                        const expiryValue = batch.expiry_date ? String(batch.expiry_date).slice(0, 10) : null;
                        const expiryDate = expiryValue ? new Date(`${expiryValue}T00:00:00`) : null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const thirtyDaysFromToday = new Date(today);
                        thirtyDaysFromToday.setDate(thirtyDaysFromToday.getDate() + 30);

                        let status = { label: 'Active', className: 'border-primary/25 bg-primary/10 text-primary' };
                        if (remaining <= 0) {
                          status = { label: 'Depleted', className: 'border-border bg-muted text-muted-foreground' };
                        } else if (expiryDate && expiryDate < today) {
                          status = { label: 'Expired', className: 'border-destructive/25 bg-destructive/10 text-destructive' };
                        } else if (expiryDate && expiryDate <= thirtyDaysFromToday) {
                          status = { label: 'Expiring soon', className: 'border-warning/30 bg-warning/10 text-warning' };
                        }

                        return (
                          <TableRow key={batch.id || `${batch.product_id}-${batch.batch_number}-${batch.created_at}`}>
                            <TableCell><div><p className="font-medium">{batch.product_name || product?.name || 'Unknown product'}</p>{product?.sku && <p className="mt-0.5 text-xs text-muted-foreground">{product.sku}</p>}</div></TableCell>
                            <TableCell className="font-mono text-sm">{batch.batch_number || '—'}</TableCell>
                            <TableCell>{batch.expiry_date ? formatBusinessDate(batch.expiry_date) : 'No expiry date'}</TableCell>
                            <TableCell className="text-right font-medium tabular-nums">{Number(batch.quantity_received || 0).toLocaleString()}</TableCell>
                            <TableCell className="text-right font-medium tabular-nums">{remaining.toLocaleString()}</TableCell>
                            <TableCell><Badge variant="outline" className={status.className}>{status.label}</Badge></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex min-h-48 flex-col items-center justify-center rounded-lg border border-dashed border-border/70 px-6 text-center">
                  <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted"><Layers3 className="h-5 w-5 text-muted-foreground" /></span>
                  <p className="font-medium">No inventory batches found</p>
                  <p className="mt-1 max-w-sm text-sm text-muted-foreground">Try another product or show depleted batches to broaden the results.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InventoryPage;
